import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useThemeContext } from '../context/ThemeContext';
import { logger } from '../services/logger';
import { useWindowShortcuts } from '../hooks';
import {
  createWindow,
  closeWindow,
  clearAllWindows,
  arrangeWindows,
  minimizeWindow,
  maximizeWindow,
  restoreWindow,
  saveLayout,
  applyLayout,
  reopenLastClosedWindow,
} from '../store/slices/windowsSlice';
import { RootState } from '../store';
import { useAppSelector, useAppDispatch } from '../store';
import { WindowManager } from '../components/windows';
import { useNotifications } from '@/hooks/useNotifications';
// Import new UI components
import { Button } from '@/components/ui/Button/Button';
import { Card } from '@/components/ui/Card/Card';
import { Input } from '@/components/ui/Input/Input';

/**
 * Test page component
 * Showcases all UI components and window management features for testing purposes
 */
const TestPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isDarkMode, toggleMode: toggleTheme, colorScheme } = useThemeContext();
  const layouts = useSelector((state: RootState) => state.windows.layouts);
  const recentlyClosedWindows = useSelector(
    (state: RootState) => state.windows.recentlyClosedWindows
  );
  const [layoutName, setLayoutName] = useState('');
  const [selectedLayout, setSelectedLayout] = useState('');
  const windowShortcuts = useWindowShortcuts();

  // Get window state from Redux store
  const windows = useAppSelector(state => state.windows.windows);
  const activeWindowId = useAppSelector(state => state.windows.activeWindowId);
  const hasRecentlyClosed = useAppSelector(state => state.windows.recentlyClosedWindows.length > 0);

  const { success, error, warning, info } = useNotifications();

  // Log that the test page was visited
  React.useEffect(() => {
    logger.info('Test page visited', { module: 'pages' });
  }, []);

  // Handle window arrangement
  const handleArrangeWindows = useCallback(() => {
    dispatch(arrangeWindows({ arrangement: 'cascade' }));
    logger.info('Windows arranged', { module: 'windows' });
  }, [dispatch]);

  // Handle layout management
  const handleSaveLayout = useCallback(() => {
    if (layoutName.trim()) {
      dispatch(saveLayout({ name: layoutName }));
      setLayoutName('');
      logger.info(`Layout saved: ${layoutName}`, { module: 'windows' });
    }
  }, [dispatch, layoutName]);

  const handleApplyLayout = useCallback(() => {
    if (selectedLayout) {
      dispatch(applyLayout(selectedLayout));
      logger.info(`Layout applied: ${selectedLayout}`, { module: 'windows' });
    }
  }, [dispatch, selectedLayout]);

  // Handle other window actions
  const handleClearWindows = useCallback(() => {
    dispatch(clearAllWindows());
    logger.info('All windows cleared', { module: 'windows' });
  }, [dispatch]);

  const handleReopenLastClosed = useCallback(() => {
    if (recentlyClosedWindows.length > 0) {
      dispatch(reopenLastClosedWindow());
      logger.info('Reopened last closed window', { module: 'windows' });
    }
  }, [dispatch, recentlyClosedWindows]);

  const createNewWindow = useCallback(
    (type: string) => {
      dispatch(
        createWindow({
          title: `New ${type}`,
          type: type as 'editor' | 'results' | 'settings' | 'help' | 'custom',
        })
      );
    },
    [dispatch]
  );

  // Handle window creation
  const handleCreateWindow = (type: 'editor' | 'results' | 'settings' | 'help' | 'custom') => {
    const titles = {
      editor: 'Email Editor',
      results: 'Analysis Results',
      settings: 'Settings',
      help: 'Help',
      custom: 'Custom Window',
    };

    dispatch(
      createWindow({
        title: titles[type],
        type,
        content: `This is a ${type} window.`,
      })
    );

    logger.info(`Created new ${type} window`, { module: 'test' });
  };

  return (
    <div className="container mx-auto px-4 py-8 animate-fadeIn">
      <Card variant="glass" className="mb-8 animate-float border-glow" hasHoverEffect>
        <h1 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          UI Component & Window System Test Page
        </h1>
        <p className="mb-4">
          This page allows you to test all UI components and window management features.
        </p>
      </Card>

      {/* Theme testing */}
      <Card variant="glass" className="mb-8 animate-slideInRight" hasAnimation>
        <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
          Theme Controls
        </h2>
        <div className="flex items-center gap-4 mb-4">
          <span>Current theme: {isDarkMode ? 'dark' : 'light'}</span>
          <Button onClick={toggleTheme} variant="glass" hasRipple isGlowing>
            Toggle Theme
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-primary text-primary-content rounded-md backdrop-blur">
            Primary
          </div>
          <div className="p-4 bg-secondary text-secondary-content rounded-md backdrop-blur">
            Secondary
          </div>
          <div className="p-4 bg-accent text-white rounded-md backdrop-blur">Accent</div>
          <div className="p-4 bg-background text-foreground rounded-md backdrop-blur">
            Background
          </div>
        </div>
      </Card>

      {/* Window management */}
      <Card variant="glass" className="mb-8 animate-slideInLeft" hasAnimation>
        <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-green-400 to-cyan-500 bg-clip-text text-transparent">
          Window Management
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <h3 className="font-bold mb-2 text-gradient-subtle">Create Windows</h3>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => createNewWindow('editor')} variant="primary" hasRipple>
                Create Editor
              </Button>
              <Button onClick={() => createNewWindow('results')} variant="secondary" hasRipple>
                Create Results
              </Button>
              <Button onClick={() => createNewWindow('settings')} variant="accent" hasRipple>
                Create Settings
              </Button>
              <Button onClick={() => createNewWindow('help')} variant="ghost" hasRipple>
                Create Help
              </Button>
            </div>
          </div>

          <div>
            <h3 className="font-bold mb-2 text-gradient-subtle">Manage Windows</h3>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleArrangeWindows} variant="glass" hasRipple>
                Arrange Windows
              </Button>
              <Button onClick={handleClearWindows} variant="outline" hasRipple>
                Close All Windows
              </Button>
              <Button
                onClick={handleReopenLastClosed}
                variant="primary"
                disabled={recentlyClosedWindows.length === 0}
                hasRipple
              >
                Reopen Last Closed
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="font-bold mb-2 text-gradient-subtle">Save Layout</h3>
            <div className="flex gap-2">
              <div className="flex-grow">
                <Input
                  variant="glass"
                  label="Layout Name"
                  value={layoutName}
                  onChange={e => setLayoutName(e.target.value)}
                  placeholder="Enter layout name"
                  isGlowing
                />
              </div>
              <Button
                onClick={handleSaveLayout}
                variant="primary"
                disabled={!layoutName.trim()}
                hasRipple
              >
                Save
              </Button>
            </div>
          </div>

          <div>
            <h3 className="font-bold mb-2 text-gradient-subtle">Apply Layout</h3>
            <div className="flex gap-2">
              <div className="flex-grow">
                <label className="block text-sm font-medium mb-1">Select Layout</label>
                <select
                  value={selectedLayout}
                  onChange={e => setSelectedLayout(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md glass dark:glass-dark backdrop-blur"
                >
                  <option value="">Choose a layout</option>
                  {Object.keys(layouts).map(name => (
                    <option key={name} value={name}>
                      {layouts[name].name}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                onClick={handleApplyLayout}
                variant="primary"
                disabled={!selectedLayout}
                hasRipple
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Keyboard Shortcuts Testing */}
      <Card variant="glass" className="mb-8 animate-slideInRight" hasAnimation>
        <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent">
          Keyboard Shortcuts
        </h2>
        <p className="mb-4">
          Test window management keyboard shortcuts. The actual shortcuts are handled globally, but
          you can also test them with these buttons.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="font-bold mb-2 text-gradient-subtle">Window Creation & Management</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Create new window</span>
                <div className="flex gap-1">
                  <Button
                    onClick={windowShortcuts.createNewWindow}
                    variant="glass"
                    size="xs"
                    hasRipple
                  >
                    Test
                  </Button>
                  <code className="px-2 py-1 bg-gray-100/10 backdrop-blur rounded text-sm glass dark:glass-dark">
                    Ctrl+N
                  </code>
                </div>
              </div>

              <div className="flex justify-between">
                <span>Close active window</span>
                <div className="flex gap-1">
                  <Button
                    onClick={windowShortcuts.closeActiveWindow}
                    variant="glass"
                    size="xs"
                    hasRipple
                  >
                    Test
                  </Button>
                  <code className="px-2 py-1 bg-gray-100/10 backdrop-blur rounded text-sm glass dark:glass-dark">
                    Ctrl+W
                  </code>
                </div>
              </div>

              <div className="flex justify-between">
                <span>Minimize/restore window</span>
                <div className="flex gap-1">
                  <Button
                    onClick={windowShortcuts.toggleMinimizeWindow}
                    variant="glass"
                    size="xs"
                    hasRipple
                  >
                    Test
                  </Button>
                  <code className="px-2 py-1 bg-gray-100/10 backdrop-blur rounded text-sm glass dark:glass-dark">
                    Alt+M
                  </code>
                </div>
              </div>

              <div className="flex justify-between">
                <span>Maximize/restore window</span>
                <div className="flex gap-1">
                  <Button
                    onClick={windowShortcuts.toggleMaximizeWindow}
                    variant="glass"
                    size="xs"
                    hasRipple
                  >
                    Test
                  </Button>
                  <code className="px-2 py-1 bg-gray-100/10 backdrop-blur rounded text-sm glass dark:glass-dark">
                    Ctrl+M
                  </code>
                </div>
              </div>

              <div className="flex justify-between">
                <span>Reopen last closed window</span>
                <div className="flex gap-1">
                  <Button
                    onClick={windowShortcuts.reopenLastClosed}
                    variant="glass"
                    size="xs"
                    hasRipple
                    disabled={recentlyClosedWindows.length === 0}
                  >
                    Test
                  </Button>
                  <code className="px-2 py-1 bg-gray-100/10 backdrop-blur rounded text-sm glass dark:glass-dark">
                    Ctrl+Z
                  </code>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-bold mb-2 text-gradient-subtle">Window Arrangement</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Cascade windows</span>
                <div className="flex gap-1">
                  <Button
                    onClick={windowShortcuts.arrangeWindowsCascade}
                    variant="glass"
                    size="xs"
                    hasRipple
                  >
                    Test
                  </Button>
                  <code className="px-2 py-1 bg-gray-100/10 backdrop-blur rounded text-sm glass dark:glass-dark">
                    Alt+Shift+↓
                  </code>
                </div>
              </div>

              <div className="flex justify-between">
                <span>Tile windows</span>
                <div className="flex gap-1">
                  <Button
                    onClick={windowShortcuts.arrangeWindowsTile}
                    variant="glass"
                    size="xs"
                    hasRipple
                  >
                    Test
                  </Button>
                  <code className="px-2 py-1 bg-gray-100/10 backdrop-blur rounded text-sm glass dark:glass-dark">
                    Alt+Shift+↑
                  </code>
                </div>
              </div>

              <div className="flex justify-between">
                <span>Arrange horizontally</span>
                <div className="flex gap-1">
                  <Button
                    onClick={windowShortcuts.arrangeWindowsHorizontal}
                    variant="glass"
                    size="xs"
                    hasRipple
                  >
                    Test
                  </Button>
                  <code className="px-2 py-1 bg-gray-100/10 backdrop-blur rounded text-sm glass dark:glass-dark">
                    Alt+Shift+→
                  </code>
                </div>
              </div>

              <div className="flex justify-between">
                <span>Arrange vertically</span>
                <div className="flex gap-1">
                  <Button
                    onClick={windowShortcuts.arrangeWindowsVertical}
                    variant="glass"
                    size="xs"
                    hasRipple
                  >
                    Test
                  </Button>
                  <code className="px-2 py-1 bg-gray-100/10 backdrop-blur rounded text-sm glass dark:glass-dark">
                    Alt+Shift+←
                  </code>
                </div>
              </div>

              <div className="flex justify-between">
                <span>Close all windows</span>
                <div className="flex gap-1">
                  <Button
                    onClick={windowShortcuts.clearAllWindows}
                    variant="glass"
                    size="xs"
                    hasRipple
                  >
                    Test
                  </Button>
                  <code className="px-2 py-1 bg-gray-100/10 backdrop-blur rounded text-sm glass dark:glass-dark">
                    None
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 glass dark:glass-dark backdrop-blur rounded-md border-glow">
          <h3 className="font-bold text-gradient-subtle mb-2">Additional Shortcuts</h3>
          <ul className="list-disc pl-5 space-y-1 text-blue-400 dark:text-blue-300 text-sm">
            <li>
              <strong>Alt+Tab / Alt+Shift+Tab</strong>: Cycle through windows
            </li>
            <li>
              <strong>Alt+1 through Alt+9</strong>: Focus specific window by number
            </li>
            <li>
              <strong>Alt+Shift+S</strong>: Save current window layout (prompts for name)
            </li>
            <li>
              <strong>Alt+Shift+L</strong>: Load a saved layout (prompts for selection)
            </li>
            <li>
              <strong>F1</strong>: Open help window
            </li>
            <li>
              <strong>Ctrl+,</strong>: Open settings window
            </li>
          </ul>
        </div>
      </Card>

      {/* UI Components */}
      <Card variant="glass" className="mb-8 animate-slideInLeft" hasAnimation>
        <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
          UI Components
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="font-bold mb-2 text-gradient-subtle">Buttons</h3>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" hasRipple>
                Primary
              </Button>
              <Button variant="secondary" hasRipple>
                Secondary
              </Button>
              <Button variant="accent" hasRipple>
                Accent
              </Button>
              <Button variant="glass" hasRipple>
                Glass
              </Button>
              <Button variant="outline" hasRipple>
                Outline
              </Button>
              <Button variant="ghost" hasRipple>
                Ghost
              </Button>
              <Button variant="primary" disabled>
                Disabled
              </Button>
              <Button variant="primary" isLoading>
                Loading
              </Button>
              <Button variant="primary" isGlowing hasRipple>
                Glowing
              </Button>
            </div>
          </div>

          <div>
            <h3 className="font-bold mb-2 text-gradient-subtle">Form Controls</h3>
            <div className="space-y-4">
              <div>
                <Input label="Text Field" placeholder="Enter some text" variant="glass" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Select Dropdown</label>
                <select className="w-full px-3 py-2 border rounded-md glass dark:glass-dark backdrop-blur">
                  <option value="">Select an option</option>
                  <option value="1">Option 1</option>
                  <option value="2">Option 2</option>
                  <option value="3">Option 3</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label>Check me</label>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card variant="glass" hasHoverEffect className="mb-8 animate-float">
        <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
          Testing Tips
        </h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>Create multiple windows to test dragging and z-index management</li>
          <li>Test window maximizing, minimizing and restoring</li>
          <li>Save layouts and apply them to verify persistence</li>
          <li>Toggle themes to verify components appear correctly in both</li>
          <li>Resize the browser window to test responsiveness</li>
          <li>Try keyboard shortcuts for window management (Alt+N, Ctrl+W, etc.)</li>
          <li>Use Alt+1-9 to focus windows by index and Alt+Tab to cycle through them</li>
        </ul>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <div className="animate-fadeIn animation-delay-100">
          <h3 className="text-xl font-medium mb-3 text-gradient-subtle">Keyboard Shortcuts</h3>
          <Card variant="glass" hasHoverEffect className="h-full">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Test keyboard shortcuts for window management. Each shortcut has a corresponding
              button.
            </p>

            {/* Window Creation and Management */}
            <div className="mb-4">
              <h4 className="font-medium bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
                Window Creation & Management
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  onClick={() => handleCreateWindow('editor')}
                  size="sm"
                  className="justify-between"
                  hasRipple
                >
                  <span>New Window</span>
                  <span className="opacity-70">(Ctrl+N)</span>
                </Button>
                <Button
                  onClick={() => dispatch(closeWindow(activeWindowId || ''))}
                  disabled={!activeWindowId}
                  size="sm"
                  className="justify-between"
                  hasRipple
                >
                  <span>Close Window</span>
                  <span className="opacity-70">(Ctrl+W)</span>
                </Button>
                <Button
                  onClick={() => dispatch(minimizeWindow(activeWindowId || ''))}
                  disabled={!activeWindowId}
                  size="sm"
                  className="justify-between"
                  hasRipple
                >
                  <span>Minimize</span>
                  <span className="opacity-70">(Alt+M)</span>
                </Button>
                <Button
                  onClick={() => {
                    const windowId = activeWindowId || '';
                    if (windowId && windows[windowId]?.isMaximized) {
                      dispatch(restoreWindow(windowId));
                    } else if (windowId) {
                      dispatch(maximizeWindow(windowId));
                    }
                  }}
                  disabled={!activeWindowId}
                  size="sm"
                  className="justify-between"
                  hasRipple
                >
                  <span>Maximize/Restore</span>
                  <span className="opacity-70">(Ctrl+M)</span>
                </Button>
                <Button
                  onClick={() => dispatch(reopenLastClosedWindow())}
                  disabled={!hasRecentlyClosed}
                  size="sm"
                  className="justify-between"
                  hasRipple
                >
                  <span>Reopen Last Closed</span>
                  <span className="opacity-70">(Ctrl+Shift+T)</span>
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <div className="animate-fadeIn animation-delay-200">
          <h3 className="text-xl font-medium mb-3 text-gradient-subtle">Window Persistence</h3>
          <Card variant="glass" hasHoverEffect className="h-full">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Test window state persistence across page reloads. Create windows in specific layouts,
              then refresh the browser to verify they are restored correctly.
            </p>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium">Current Window Count:</span>
                <span className="px-3 py-1 glass dark:glass-dark backdrop-blur text-blue-500 rounded-md border-glow">
                  {Object.values(windows).filter(w => w.isOpen).length} open windows
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="font-medium">Local Storage:</span>
                <Button
                  onClick={() => {
                    window.location.reload();
                  }}
                  variant="glass"
                  size="sm"
                  hasRipple
                >
                  Reload Page
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-2">
                <Button
                  onClick={() => {
                    // Create a test window set for persistence testing
                    dispatch(clearAllWindows());
                    dispatch(
                      createWindow({
                        title: 'Persistence Test 1',
                        type: 'editor',
                        position: { x: 100, y: 100, width: 300, height: 200 },
                      })
                    );
                    dispatch(
                      createWindow({
                        title: 'Persistence Test 2',
                        type: 'results',
                        position: { x: 450, y: 150, width: 350, height: 250 },
                      })
                    );
                    dispatch(saveLayout({ name: 'Persistence Test' }));
                  }}
                  variant="primary"
                  size="sm"
                  hasRipple
                >
                  Create Test Windows
                </Button>
                <Button
                  onClick={() => {
                    dispatch(clearAllWindows());
                    logger.info('Cleared all windows for persistence testing', { module: 'test' });
                  }}
                  variant="outline"
                  size="sm"
                  hasRipple
                >
                  Clear All Windows
                </Button>
              </div>

              <div className="glass dark:glass-dark backdrop-blur text-xs p-2 border rounded border-yellow-200/20 bg-yellow-50/10 text-yellow-500 mt-3">
                <p>To test persistence:</p>
                <ol className="list-decimal list-inside mt-1 space-y-1">
                  <li>Create test windows or arrange windows how you want</li>
                  <li>Click "Reload Page" to refresh the browser</li>
                  <li>Verify windows are restored in the same positions</li>
                </ol>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Notification Testing Section */}
      <Card
        variant="glass"
        className="mb-8 mt-8 animate-slideInRight animation-delay-300"
        hasAnimation
      >
        <h2 className="text-xl font-semibold mb-3 bg-gradient-to-r from-green-400 to-teal-500 bg-clip-text text-transparent">
          Notification Testing
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Button
            onClick={() => success('Success', 'Operation completed successfully.')}
            variant="glass"
            hasRipple
          >
            Show Success
          </Button>

          <Button
            onClick={() => error('Error', 'An error occurred while processing your request.')}
            variant="glass"
            hasRipple
          >
            Show Error
          </Button>

          <Button
            onClick={() => warning('Warning', 'Please review the information before proceeding.')}
            variant="glass"
            hasRipple
          >
            Show Warning
          </Button>

          <Button
            onClick={() => info('Information', 'This is an informational message.')}
            variant="glass"
            hasRipple
          >
            Show Info
          </Button>

          <Button
            onClick={() => {
              success('With Action', 'This notification has an action button.', {
                actions: [
                  {
                    label: 'Take Action',
                    onClick: () => alert('Action taken!'),
                  },
                ],
              });
            }}
            variant="glass"
            hasRipple
          >
            With Action
          </Button>

          <Button
            onClick={() => {
              success('Multiple Notifications', 'First notification');
              setTimeout(() => warning('Multiple Notifications', 'Second notification'), 500);
              setTimeout(() => info('Multiple Notifications', 'Third notification'), 1000);
              setTimeout(() => error('Multiple Notifications', 'Fourth notification'), 1500);
            }}
            variant="glass"
            hasRipple
          >
            Show Multiple
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default TestPage;
